import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Add, Delete, Save, PictureAsPdf } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ja from 'date-fns/locale/ja';

interface DeliveryItem {
  id?: string;
  item_name: string;
  quantity: number;
  unit: string;
  unit_price: number;
  tax_rate: number;
}

export default function DeliveryEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'create';

  const [delivery, setDelivery] = useState<any>({
    delivery_date: new Date().toISOString().split('T')[0],
    issue_date: new Date().toISOString().split('T')[0],
    notes: '',
    status: 'draft',
  });
  const [items, setItems] = useState<DeliveryItem[]>([]);
  const [orderInfo, setOrderInfo] = useState<any>(null);
  const [workOrderDialog, setWorkOrderDialog] = useState(false);
  const [workOrders, setWorkOrders] = useState<any[]>([]);

  useEffect(() => {
    if (!isNew) {
      loadDelivery();
    } else {
      loadWorkOrders();
    }
  }, [id]);

  const loadDelivery = async () => {
    const result = await window.electronAPI.invoke('delivery:get', id);
    if (result) {
      setDelivery(result);
      setItems(result.items || []);
    }
  };

  const loadWorkOrders = async () => {
    const result = await window.electronAPI.invoke('workOrder:list', { status: 'approved' });
    setWorkOrders(result);
  };

  const handleCreateFromOrder = async (workOrderId: string) => {
    try {
      const payload = {
        delivery_date: delivery.delivery_date,
        issue_date: delivery.issue_date,
        notes: delivery.notes,
      };
      const newId = await window.electronAPI.invoke('delivery:createFromOrder', workOrderId, payload);
      setWorkOrderDialog(false);
      navigate(`/delivery/edit/${newId}`);
    } catch (error) {
      console.error('作成エラー:', error);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...delivery,
        items,
      };
      if (isNew) {
        // 新規作成時は注文請書選択が必要
        return;
      }
      await window.electronAPI.invoke('delivery:update', id, payload);
      navigate('/delivery');
    } catch (error) {
      console.error('保存エラー:', error);
    }
  };

  const handlePrintPdf = async () => {
    try {
      const pdfPath = await window.electronAPI.invoke('delivery:printPdf', id);
      window.electronAPI.invoke('shell:openPath', pdfPath);
    } catch (error) {
      console.error('PDF出力エラー:', error);
    }
  };

  const addItem = () => {
    setItems([...items, { item_name: '', quantity: 1, unit: '式', unit_price: 0, tax_rate: 10 }]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...items];
    (newItems[index] as any)[field] = value;
    setItems(newItems);
  };

  const totalAmount = items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  const taxAmount = items.reduce((sum, item) => sum + item.quantity * item.unit_price * (item.tax_rate / 100), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">{isNew ? '納品書作成' : '納品書編集'}</Typography>
        <Box>
          {!isNew && (
            <Button startIcon={<PictureAsPdf />} onClick={handlePrintPdf} sx={{ mr: 1 }}>
              PDF出力
            </Button>
          )}
          <Button variant="contained" startIcon={<Save />} onClick={handleSave}>
            保存
          </Button>
        </Box>
      </Box>

      {isNew && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Button variant="outlined" onClick={() => setWorkOrderDialog(true)}>
            注文請書から作成
          </Button>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>基本情報</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
            <DatePicker
              label="引渡日"
              value={new Date(delivery.delivery_date)}
              onChange={(date) => setDelivery({ ...delivery, delivery_date: date ? date.toISOString().split('T')[0] : '' })}
              renderInput={(params) => <TextField {...params} />}
            />
            <DatePicker
              label="発行日"
              value={new Date(delivery.issue_date)}
              onChange={(date) => setDelivery({ ...delivery, issue_date: date ? date.toISOString().split('T')[0] : '' })}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </Box>
        <TextField
          label="備考"
          multiline
          rows={3}
          value={delivery.notes || ''}
          onChange={(e) => setDelivery({ ...delivery, notes: e.target.value })}
          fullWidth
          sx={{ mt: 2 }}
        />
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">明細</Typography>
          <Button startIcon={<Add />} onClick={addItem}>行追加</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>品名</TableCell>
                <TableCell align="right">数量</TableCell>
                <TableCell>単位</TableCell>
                <TableCell align="right">単価</TableCell>
                <TableCell align="right">税率(%)</TableCell>
                <TableCell align="right">金額</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      size="small"
                      value={item.item_name}
                      onChange={(e) => updateItem(index, 'item_name', e.target.value)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', Number(e.target.value))}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={item.unit}
                      onChange={(e) => updateItem(index, 'unit', e.target.value)}
                      sx={{ width: 60 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', Number(e.target.value))}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={item.tax_rate}
                      onChange={(e) => updateItem(index, 'tax_rate', Number(e.target.value))}
                      sx={{ width: 60 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    ¥{(item.quantity * item.unit_price).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => removeItem(index)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ textAlign: 'right', mt: 2 }}>
          <Typography variant="h6">
            小計: ¥{totalAmount.toLocaleString()}
          </Typography>
          <Typography variant="h6">
            消費税: ¥{taxAmount.toLocaleString()}
          </Typography>
          <Typography variant="h5" color="error">
            合計: ¥{(totalAmount + taxAmount).toLocaleString()}
          </Typography>
        </Box>
      </Paper>

      <Dialog open={workOrderDialog} onClose={() => setWorkOrderDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>注文請書を選択</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>注文No</TableCell>
                  <TableCell>顧客名</TableCell>
                  <TableCell>件名</TableCell>
                  <TableCell>日付</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workOrders.map((order: any) => (
                  <TableRow key={order.id}>
                    <TableCell>{order.doc_no}</TableCell>
                    <TableCell>{order.customer_name}</TableCell>
                    <TableCell>{order.title}</TableCell>
                    <TableCell>{order.order_date}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => handleCreateFromOrder(order.id)}>
                        選択
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkOrderDialog(false)}>キャンセル</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
